// src/pages/Login.jsx
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Stack,
  Text,
  Link,
  Image,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import Auth from "../utils/auth";
import Logo from "../assets/images/drhvac.svg";
import { loginUser, updatePassword, forgotPassword } from "../utils/api"; // ✅ import forgotPassword
import "../App.css";

const LoginForm = () => {
  const navigate = useNavigate();

  // Login fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordShown, setPasswordShown] = useState(false);

  // Reset mode
  const [showResetMode, setShowResetMode] = useState(false);
  const [resetStep, setResetStep] = useState("request"); // ✅ "request" | "reset"
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  // Message state
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error"); // ✅ "error" | "success"

  useEffect(() => {
    setEmail("");
    setPassword("");
    setOtp("");
    setNewPassword("");
    setConfirmNewPassword("");
    setMessage("");
    setMessageType("error");
    setShowResetMode(false);
    setResetStep("request");
  }, []);

  const togglePasswordVisibility = () => {
    setPasswordShown((v) => !v);
  };

  const startResetFlow = () => {
    setShowResetMode(true);
    setResetStep("request");
    setOtp("");
    setNewPassword("");
    setConfirmNewPassword("");
    setMessage("");
    setMessageType("error");
  };

  const backToLogin = () => {
    setShowResetMode(false);
    setResetStep("request");
    setOtp("");
    setNewPassword("");
    setConfirmNewPassword("");
    setMessage("");
    setMessageType("error");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setMessageType("error");

    const formData = { email, password };

    try {
      const response = await loginUser(formData);
      if (!response.ok) {
        // Try to show backend message if present
        let msg = "Login failed.";
        try {
          const data = await response.json();
          msg = data.message || msg;
        } catch {}
        setMessage(msg);
        return;
      }

      const { token, role } = await response.json();
      localStorage.setItem("drhvac_session", token);
      localStorage.setItem("user_role", role);

      Auth.login(token);

      if (role === "admin") navigate("/approve");
      else navigate("/rating");
    } catch (err) {
      console.error("Login error:", err);
      setMessage("An error occurred while logging in.");
    }
  };

  // ✅ Step 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setMessage("");
    setMessageType("error");

    try {
      const res = await forgotPassword({ email });

      const raw = await res.text();
      let data = {};
      try {
        data = JSON.parse(raw);
      } catch {}

      if (!res.ok) {
        setMessage(data.message || raw || "Failed to send OTP.");
        return;
      }

      // ✅ success
      setMessageType("success");
      setMessage(data.message || "OTP sent. Check your email.");

      // ✅ optionally persist email for the /otp page
      localStorage.setItem("reset_email", email);

      // ✅ redirect to OTP page
      navigate("/otp");
    } catch (err) {
      console.error("Forgot password error:", err);
      setMessage("Network error sending OTP.");
    }
  };

  // ✅ Step 2: Update password
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setMessage("");
    setMessageType("error");

    if (newPassword !== confirmNewPassword) {
      setMessage("New passwords do not match.");
      return;
    }

    try {
      const response = await updatePassword({ email, otp, newPassword });

      let data = null;
      try {
        data = await response.json();
      } catch {}

      if (response.ok) {
        setMessageType("success");
        setMessage("Password updated! Please log in with your new password.");
        // back to login + clear reset fields
        setShowResetMode(false);
        setResetStep("request");
        setOtp("");
        setNewPassword("");
        setConfirmNewPassword("");
      } else {
        setMessage(data?.message || "Failed to update password.");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      setMessage("An error occurred while updating the password.");
    }
  };

  const onSubmit = !showResetMode
    ? handleFormSubmit
    : resetStep === "request"
      ? handleSendOtp
      : handlePasswordReset;

  return (
    <Flex justify="center" align="center" height="100vh">
      <Box
        w={500}
        p={12}
        bg="white"
        boxShadow="0px 10px 20px rgba(0, 0, 0, 0.1), 0px 6px 6px rgba(0, 0, 0, 0.1)"
        rounded="lg"
      >
        <Flex justify="space-between" align="center" mb={8}>
          <Image w={100} src={Logo} alt="Logo" />
          <Heading color="#1c4b7e" size="md">
            {!showResetMode
              ? "Login"
              : resetStep === "request"
                ? "Forgot Password"
                : "Reset Password"}
          </Heading>
        </Flex>

        <Stack spacing={4} as="form" onSubmit={onSubmit}>
          {/* Email always shown (needed for forgot/reset) */}
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            borderRadius="0"
            variant="unstyled"
            borderBottom="2px solid #1c4b7e"
            _focus={{ boxShadow: "none" }}
          />

          {/* Login fields */}
          {!showResetMode && (
            <Box position="relative">
              <Input
                type={passwordShown ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                borderRadius="0"
                variant="unstyled"
                borderBottom="2px solid #1c4b7e"
                _focus={{ boxShadow: "none" }}
              />
              <Box
                position="absolute"
                top="50%"
                right="0"
                transform="translateY(-50%)"
                cursor="pointer"
                onClick={togglePasswordVisibility}
              >
                <Icon icon="mdi:eye" />
              </Box>
            </Box>
          )}

          {/* Reset mode: Step 2 fields */}
          {showResetMode && resetStep === "reset" && (
            <>
              <Input
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                variant="unstyled"
                borderBottom="2px solid #1c4b7e"
                _focus={{ boxShadow: "none" }}
              />
              <Input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                variant="unstyled"
                borderBottom="2px solid #1c4b7e"
                _focus={{ boxShadow: "none" }}
              />
              <Input
                type="password"
                placeholder="Confirm New Password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                variant="unstyled"
                borderBottom="2px solid #1c4b7e"
                _focus={{ boxShadow: "none" }}
              />
            </>
          )}

          <Button
            type="submit"
            bg="#00aeef"
            color="white"
            _hover={{ bg: "#1c4b7e" }}
            mt={6}
          >
            {!showResetMode
              ? "Login"
              : resetStep === "request"
                ? "Send OTP"
                : "Reset Password"}
          </Button>

          {message && (
            <Text color={messageType === "success" ? "green.500" : "red.500"}>
              {message}
            </Text>
          )}

          <Flex justify="space-between" mt={4}>
            {!showResetMode ? (
              <Link color="blue.500" onClick={startResetFlow}>
                Forgot Password?
              </Link>
            ) : (
              <Link color="blue.500" onClick={backToLogin}>
                Back to Login
              </Link>
            )}

            <Link color="blue.500" href="/signup">
              Sign Up
            </Link>
          </Flex>
        </Stack>
      </Box>
    </Flex>
  );
};

export default LoginForm;
